import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { FaEdit, FaTrash, FaPlus, FaSignOutAlt, FaSave, FaSearch, FaEye, FaEyeSlash, FaImage, FaImages, FaTimes, FaUpload, FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaBold, FaItalic, FaHeading, FaListUl, FaListOl, FaLink, FaCode, FaQuoteRight, FaTag, FaMagic, FaLanguage, FaFilePdf, FaBuilding, FaHome, FaHardHat, FaProjectDiagram, FaWindowRestore, FaDraftingCompass, FaStore, FaArrowLeft, FaCloudUploadAlt, FaUserShield, FaDatabase, FaThList, FaChevronRight, FaFileInvoiceDollar } from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import StoreManagement from './StoreManagement'
import UserConsole from './UserConsole'
import MondayManagement from './MondayManagement'
import WorkflowMap from './WorkflowMap'
import EstimateBuilder from './EstimateBuilder'
import { RoleProvider } from '../../hooks/useRole'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import ServiceHealthBar from '../../components/ServiceHealthBar'

const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '')
}

// Image compression utility
const compressImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
                const canvas = document.createElement('canvas')
                let width = img.width
                let height = img.height

                // Calculate new dimensions while maintaining aspect ratio
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width)
                        width = maxWidth
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height)
                        height = maxHeight
                    }
                }

                canvas.width = width
                canvas.height = height

                const ctx = canvas.getContext('2d')
                ctx.drawImage(img, 0, 0, width, height)

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(new File([blob], file.name, { type: file.type }))
                        } else {
                            reject(new Error('Compression failed'))
                        }
                    },
                    file.type,
                    quality
                )
            }
            img.onerror = reject
            img.src = e.target.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

export default function AdminDashboard() {
    const { t, i18n } = useTranslation()
    const [session, setSession] = useState(null)
    const [posts, setPosts] = useState([])
    const [filteredPosts, setFilteredPosts] = useState([])
    const [editingPost, setEditingPost] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')

    // Login State
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [honeyPot, setHoneyPot] = useState('')

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        image_url: '',
        is_published: false,
        tags: [],
        slug_origin: null, // 'en' | 'es' | null
        title_en: '',
        content_en: '',
        excerpt_en: '',
        title_es: '',
        content_es: '',
        excerpt_es: ''
    })

    // Image Upload State
    const [uploadingImage, setUploadingImage] = useState(false)
    const [uploadingInlineImage, setUploadingInlineImage] = useState(false)
    const [imagePreview, setImagePreview] = useState(null)
    const [dragActive, setDragActive] = useState(false)
    const [tagInput, setTagInput] = useState('')

    // Translation State
    const [activeTab, setActiveTab] = useState('es') // 'en' | 'es'
    const [isTranslating, setIsTranslating] = useState(false)
    const [showFloatingActions, setShowFloatingActions] = useState(false)

    // Toast Notification State
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' })
    const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null })
    const [savingAction, setSavingAction] = useState(null) // 'draft' | 'publish' | null

    // Portfolio & Routing State
    const location = useLocation()
    const navigate = useNavigate()

    // Derive activeView from current URL path
    const activeView = location.pathname.includes('/admin/products') ? 'store'
        : location.pathname.includes('/admin/portfolio') ? 'portfolios'
            : location.pathname.includes('/admin/console') ? 'console'
                : location.pathname.includes('/admin/monday') ? 'monday'
                    : location.pathname.includes('/admin/workflow') ? 'workflow'
                        : location.pathname.includes('/admin/estimator') ? 'estimator'
                            : location.pathname.includes('/admin/blog') ? 'blog'
                            : 'home'

    const setActiveView = (view) => {
        if (view === 'store') navigate('/admin/products')
        else if (view === 'portfolios') navigate('/admin/portfolio')
        else if (view === 'console') navigate('/admin/console')
        else if (view === 'monday') navigate('/admin/monday')
        else if (view === 'workflow') navigate('/admin/workflow')
        else if (view === 'estimator') navigate('/admin/estimator')
        else if (view === 'blog') navigate('/admin/blog')
        else navigate('/admin')
    }
    const [portfolios, setPortfolios] = useState([])
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState('main')
    const [uploadingPortfolio, setUploadingPortfolio] = useState(false)
    const [portfolioDragActive, setPortfolioDragActive] = useState(false)
    const portfolioFileInputRef = useRef(null)

    // Business Units Configuration
    const BUSINESS_UNITS = [
        { key: 'main', label: t('admin.portfolios.business_units.main'), icon: FaBuilding },
        { key: 'architecture', label: t('admin.portfolios.business_units.architecture'), icon: FaDraftingCompass },
        { key: 'construction', label: t('admin.portfolios.business_units.construction'), icon: FaHardHat },
        { key: 'windows', label: t('admin.portfolios.business_units.windows'), icon: FaWindowRestore },
        { key: 'roofing', label: t('admin.portfolios.business_units.roofing'), icon: FaHome }
    ]

    // Textarea ref for markdown editor
    const contentTextareaRef = useRef(null)
    const fileInputRef = useRef(null)
    const inlineImageInputRef = useRef(null)

    // Handle Scroll for Floating Actions
    useEffect(() => {
        const handleScroll = () => {
            setShowFloatingActions(window.scrollY > 150)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const [role, setRole] = useState('viewer')
    const [permissions, setPermissions] = useState({})
    const [roleLoading, setRoleLoading] = useState(true)

    // Fetch auth data and posts when component mounts
    useEffect(() => {
        const fetchAuth = async () => {
            setRoleLoading(true)
            try {
                const [{ data: userRole }, { data: perms }] = await Promise.all([
                    supabase.rpc('get_user_role'),
                    supabase.rpc('get_user_permissions'),
                ])
                setRole(userRole || 'viewer')
                setPermissions(perms || {})
            } catch (err) {
                console.error('Auth fetch error:', err)
                setRole('viewer')
                setPermissions({})
            } finally {
                setRoleLoading(false)
            }
        }

        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            if (session) fetchAuth()
            else setRoleLoading(false)
        })

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session)
            if (session) fetchAuth()
            else setRoleLoading(false)
        })

        return () => subscription.unsubscribe()
    }, [])

    const isSuperAdmin      = role === 'super_admin'
    const isEditor           = isSuperAdmin || !!permissions['admin.blog.access'] || !!permissions['admin.portfolios.access'] || !!permissions['admin.monday.access']
    const isStoreManager     = isSuperAdmin || !!permissions['admin.products.access']
    const isPM               = isSuperAdmin || !!permissions['admin.workflow.access'] || !!permissions['admin.estimator.access']
    const isClient           = role === 'client'
    const isEstimator        = isSuperAdmin || !!permissions['admin.estimator.access']
    const isArchEstimator    = !!permissions['admin.estimator.architecture']
    const isConstrEstimator  = !!permissions['admin.estimator.construction']
    const isAnyEstimator     = isEstimator || isArchEstimator || isConstrEstimator

    useEffect(() => {
        if (searchTerm) {
            const q = searchTerm.toLowerCase()
            setFilteredPosts(posts.filter(post => {
                const title = (post.title_en || post.title_es || post.title || '').toLowerCase()
                const slug = (post.slug || '').toLowerCase()
                return title.includes(q) || slug.includes(q)
            }))
        } else {
            setFilteredPosts(posts)
        }
    }, [searchTerm, posts])

    // Update image preview when formData.image_url changes
    useEffect(() => {
        if (formData.image_url) {
            setImagePreview(formData.image_url)
        } else {
            setImagePreview(null)
        }
    }, [formData.image_url])

    // Keyboard Shortcuts for Markdown Formatting
    useEffect(() => {
        const handleKeyDown = (e) => {
            //  Only trigger in editor view
            if (!editingPost) return

            // Ctrl/Cmd + B = Bold
            if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
                e.preventDefault()
                formatBold()
            }
            // Ctrl/Cmd + I = Italic
            if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
                e.preventDefault()
                formatItalic()
            }
            // Ctrl/Cmd + K = Link
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault()
                formatLink()
            }
            // Ctrl/Cmd + S = Save
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault()
                handleSave(e, false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [editingPost, formData])

    const handleLogin = async (e) => {
        e.preventDefault()
        if (honeyPot) {
            setLoading(true)
            setTimeout(() => setLoading(false), 2000)
            return
        }
        setLoading(true)
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) {
            showToast(t('auth.error') + ': ' + error.message, 'error')
        } else {
            showToast(t('auth.success'), 'success')
        }
        setLoading(false)
    }

    const handleLogout = async () => await supabase.auth.signOut()

    const fetchPosts = async (silent = false) => {
        if (!silent) {
            if (posts.length === 0) setLoading(true)
            else showToast(t('admin.refreshing_data'), 'info')
        }
        const { data } = await supabase
            .from('posts')
            .select('*')
            .order('created_at', { ascending: false })
        if (data) {
            setPosts(data)
            setFilteredPosts(data)
        }
        if (!silent) setLoading(false)
    }

    const handleCreateNew = () => {
        setEditingPost('new')
        setFormData({
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            image_url: '',
            is_published: false,
            tags: [],
            slug_origin: null,
            title_en: '',
            content_en: '',
            excerpt_en: '',
            title_es: '',
            content_es: '',
            excerpt_es: ''
        })
        setImagePreview(null)
        setTagInput('')
        setActiveTab('es')
    }

    const handleEdit = (post) => {
        setEditingPost(post.id)
        // Determine slug_origin: use saved value, or detect from slug text
        let detectedOrigin = post.slug_origin
        if (!detectedOrigin && post.slug) {
            // If slug contains Spanish characters (ñ, accented vowels) → likely Spanish origin
            const hasSpanishChars = /[ñáéíóúü]/i.test(post.slug)
            detectedOrigin = hasSpanishChars ? 'es' : 'en'
        }
        setFormData({
            ...post,
            tags: post.tags || [],
            // For pre-migration posts, populate _en from legacy fields
            title_en: post.title_en || post.title || '',
            content_en: post.content_en || post.content || '',
            excerpt_en: post.excerpt_en || post.excerpt || '',
            title_es: post.title_es || '',
            content_es: post.content_es || '',
            excerpt_es: post.excerpt_es || '',
            slug_origin: detectedOrigin || null
        })
        setImagePreview(post.image_url || null)
        setTagInput('')
        // Default to the primary language tab (the slug language)
        setActiveTab(detectedOrigin || 'es')
    }

    const handleDelete = async (id) => {
        showConfirm(t('admin.deleteConfirm'), async () => {
            const { error } = await supabase.from('posts').delete().eq('id', id)
            if (!error) {
                fetchPosts()
                showToast(t('admin.success.deletePost'), 'success')
            } else {
                showToast(t('admin.error.deletePost'), 'error')
            }
        })
    }

    const handleSave = async (e, publishNow = false) => {
        e.preventDefault()

        // Prevent double-clicks or concurrent actions
        if (savingAction) return

        // Set specific action
        setSavingAction(publishNow ? 'publish' : 'draft')

        try {
            let slugToSave = formData.slug
            if (!slugToSave) {
                // Generate slug from primary language title
                const primaryTitle = formData.slug_origin === 'es' ? formData.title_es : (formData.title_en || formData.title)
                if (primaryTitle) slugToSave = slugify(primaryTitle)
            }

            const payload = {
                ...formData,
                slug: slugToSave,
                is_published: publishNow || formData.is_published,
                updated_at: new Date().toISOString()
            }

            let error
            if (editingPost === 'new') {
                const { data, error: insertError } = await supabase
                    .from('posts')
                    .insert([payload])
                    .select()
                    .single()
                if (data) {
                    // If it's a draft, keep editing the newly created post
                    if (!payload.is_published) {
                        setEditingPost(data.id)
                        setFormData(data)
                    }
                }
                error = insertError
            } else {
                const { error: updateError } = await supabase
                    .from('posts')
                    .update(payload)
                    .eq('id', editingPost)
                error = updateError
            }

            if (error) {
                if (error.code === '23505') {
                    showToast(t('admin.error.duplicateSlug'), 'error')
                } else {
                    showToast(t('admin.save') + ' ' + t('common.error') + ': ' + error.message, 'error')
                }
            } else {
                await fetchPosts()
                showToast(t('admin.success.savePost'), 'success')

                // Only close editor if publishing
                if (publishNow || formData.is_published) {
                    setEditingPost(null)
                    setImagePreview(null)
                }
            }
        } catch (err) {
            console.error('Save error:', err)
            showToast(t('common.error') + ': ' + err.message, 'error')
        } finally {
            setSavingAction(null)
        }
    }

    // Image Upload Handlers
    const validateImage = (file) => {
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
        const maxSize = 10 * 1024 * 1024 // 10MB before compression

        if (!validTypes.includes(file.type)) {
            showToast(t('admin.error.invalidFileType'), 'error')
            return false
        }

        if (file.size > maxSize) {
            showToast(t('admin.error.fileTooLarge'), 'error')
            return false
        }

        return true
    }

    const handleImageUpload = async (file) => {
        if (!validateImage(file)) return

        setUploadingImage(true)
        try {
            // Compress the image
            const compressedFile = await compressImage(file)

            // Generate unique filename
            const fileExt = file.name.split('.').pop()
            const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
            const filePath = `${fileName}`

            //  Upload to Supabase Storage
            const { error } = await supabase.storage
                .from('blog-images')
                .upload(filePath, compressedFile, {
                    cacheControl: '3600',
                    upsert: false
                })

            if (error) throw error

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('blog-images')
                .getPublicUrl(filePath)

            setFormData({ ...formData, image_url: publicUrl })
            setImagePreview(publicUrl)
            showToast(t('admin.success.imageUpload'), 'success')
        } catch (error) {
            console.error('Upload error:', error)
            showToast(t('admin.error.imageUpload') + ': ' + error.message, 'error')
        } finally {
            setUploadingImage(false)
        }
    }

    const handleDrag = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageUpload(e.dataTransfer.files[0])
        }
    }

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleImageUpload(e.target.files[0])
        }
    }

    const removeImage = () => {
        setFormData({ ...formData, image_url: '' })
        setImagePreview(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    // Toast Functions
    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type })
        setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 4000)
    }

    const showConfirm = (message, onConfirm) => {
        setConfirmDialog({ show: true, message, onConfirm })
    }

    const handleConfirm = () => {
        if (confirmDialog.onConfirm) confirmDialog.onConfirm()
        setConfirmDialog({ show: false, message: '', onConfirm: null })
    }

    const handleCancelConfirm = () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null })
    }

    // Markdown Formatting Functions
    const insertMarkdown = (before, after = '') => {
        const textarea = contentTextareaRef.current
        if (!textarea) return

        // Update content based on active tab
        const currentContent = activeTab === 'en' ? formData.content_en || formData.content : formData.content_es
        const contentField = activeTab === 'en' ? 'content_en' : 'content_es'

        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const selectedText = currentContent.substring(start, end)
        const beforeText = currentContent.substring(0, start)
        const afterText = currentContent.substring(end)

        const newText = beforeText + before + selectedText + after + afterText

        // Update both specific field and generic content for consistency
        const updates = { [contentField]: newText }
        if (activeTab === 'en') updates.content = newText

        setFormData({ ...formData, ...updates })

        setTimeout(() => {
            textarea.focus()
            const newCursorPos = start + before.length + selectedText.length + after.length
            textarea.setSelectionRange(newCursorPos, newCursorPos)
        }, 0)
    }



    const formatBold = () => insertMarkdown('**', '**')
    const formatItalic = () => insertMarkdown('*', '*')
    const formatHeading = () => insertMarkdown('## ', '')
    const formatBulletList = () => insertMarkdown('- ', '')
    const formatNumberList = () => insertMarkdown('1. ', '')
    const formatLink = () => insertMarkdown('[', '](url)')
    const formatCode = () => insertMarkdown('`', '`')
    const formatQuote = () => insertMarkdown('> ', '')

    // Inline Image Upload (inserts into content)
    const handleInlineImageUpload = async (file) => {
        if (!validateImage(file)) return

        setUploadingInlineImage(true)
        try {
            const compressedFile = await compressImage(file)
            const fileExt = file.name.split('.').pop()
            const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
            const filePath = `${fileName}`

            const { error } = await supabase.storage
                .from('blog-images')
                .upload(filePath, compressedFile, {
                    cacheControl: '3600',
                    upsert: false
                })

            if (error) throw error

            const { data: { publicUrl } } = supabase.storage
                .from('blog-images')
                .getPublicUrl(filePath)

            // Insert markdown image at cursor position
            insertMarkdown(`\n![image](${publicUrl})\n`, '')
            showToast(t('admin.success.imageUpload'), 'success')
        } catch (error) {
            console.error('Inline image upload error:', error)
            showToast(t('admin.error.imageUpload') + ': ' + error.message, 'error')
        } finally {
            setUploadingInlineImage(false)
        }
    }

    const handleInlineImageInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleInlineImageUpload(e.target.files[0])
            e.target.value = '' // Reset so same file can be selected again
        }
    }

    // Tag management
    // Tag management
    const handleAddTag = (value) => {
        const tag = value.trim().toLowerCase()
        const currentTags = formData.tags || []

        if (tag && !currentTags.includes(tag)) {
            setFormData({ ...formData, tags: [...currentTags, tag] })
        }
        setTagInput('')
    }

    const handleRemoveTag = (tagToRemove) => {
        const currentTags = formData.tags || []
        setFormData({ ...formData, tags: currentTags.filter(t => t !== tagToRemove) })
    }

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            handleAddTag(tagInput)
        }
        const currentTags = formData.tags || []
        if (e.key === 'Backspace' && !tagInput && currentTags.length > 0) {
            handleRemoveTag(currentTags[currentTags.length - 1])
        }
    }

    // AI Translation — direct GLM API call
    const translateFields = async (fields, sourceLang, targetLang) => {
        if (!fields || Object.keys(fields).length === 0) return {}

        const { data, error } = await supabase.functions.invoke('translate-post', {
            body: {
                fields,
                sourceLang,
                targetLang
            }
        })

        if (error) {
            console.error('Translation function error:', error)
            throw new Error(error.message || 'Translation failed')
        }

        if (!data || (!data.translatedFields && !data)) {
            throw new Error('Translation returned no data')
        }

        // Defensively unwrap in case the LLM returned it nested (e.g., {translatedFields: {translatedFields: {...}}})
        let result = data.translatedFields || data
        if (result && result.translatedFields) {
            result = result.translatedFields
        }

        return result
    }

    const handleTranslate = async () => {
        const sourceLang = activeTab
        const targetLang = activeTab === 'en' ? 'es' : 'en'

        const titleToTranslate = activeTab === 'en' ? (formData.title_en || formData.title) : formData.title_es
        const excerptToTranslate = activeTab === 'en' ? (formData.excerpt_en || formData.excerpt) : formData.excerpt_es
        const contentToTranslate = activeTab === 'en' ? (formData.content_en || formData.content) : formData.content_es

        if (!titleToTranslate && !excerptToTranslate && !contentToTranslate) {
            showToast(t('admin.language.translate_empty') || 'Please write some content first!', 'info')
            return
        }

        setIsTranslating(true)
        try {
            const fieldsToTranslate = {}
            if (titleToTranslate) fieldsToTranslate.title = titleToTranslate
            if (excerptToTranslate) fieldsToTranslate.excerpt = excerptToTranslate
            if (contentToTranslate) fieldsToTranslate.content = contentToTranslate

            // Translate tags from the active tab
            const currentTags = formData[`tags_${sourceLang}`] || []
            if (currentTags.length > 0) fieldsToTranslate.tags = currentTags

            const translatedFields = await translateFields(fieldsToTranslate, sourceLang, targetLang)

            setFormData(prev => ({
                ...prev,
                [`title_${targetLang}`]: translatedFields.title || prev[`title_${targetLang}`],
                [`excerpt_${targetLang}`]: translatedFields.excerpt || prev[`excerpt_${targetLang}`],
                [`content_${targetLang}`]: translatedFields.content || prev[`content_${targetLang}`]
            }))

            // Switch tab to show result
            setActiveTab(targetLang)
            showToast(t('admin.language.translate_success') || 'Translation complete ✨', 'success')

        } catch (error) {
            console.error('Translation error:', error)
            showToast(t('admin.language.translate_error') + ': ' + error.message, 'error')
        } finally {
            setIsTranslating(false)
        }
    }

    // Update form data based on active tab editing
    const handleContentChange = (val) => {
        const field = activeTab === 'en' ? 'content_en' : 'content_es'
        setFormData(prev => ({ ...prev, [field]: val }))
    }

    const handleTitleChange = (val) => {
        const updates = { [`title_${activeTab}`]: val }
        // Auto-generate slug if it's currently empty or belongs to this language
        if (!formData.slug || formData.slug_origin === activeTab || !formData.slug_origin) {
            updates.slug = slugify(val)
            updates.slug_origin = val ? activeTab : null
        }
        setFormData(prev => ({ ...prev, ...updates }))
    }

    const handleSlugChange = (val) => {
        const slugVal = val.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
        setFormData(prev => ({
            ...prev,
            slug: slugVal,
            slug_origin: slugVal ? activeTab : null
        }))
    }

    const handleExcerptChange = (val) => {
        const field = activeTab === 'en' ? 'excerpt_en' : 'excerpt_es'
        setFormData(prev => ({ ...prev, [field]: val }))
    }

    // --- PORTFOLIO MANAGEMENT FUNCTIONS ---

    const fetchPortfolios = async (silent = false) => {
        const { data, error } = await supabase
            .from('portfolios')
            .select('*')
            .order('business_unit', { ascending: true })
        if (data) setPortfolios(data)
        if (error) console.error('Error fetching portfolios:', error)
    }

    const validatePdf = (file) => {
        if (file.type !== 'application/pdf') {
            showToast(t('admin.portfolios.error.invalid_type'), 'error')
            return false
        }
        const maxSize = 28 * 1024 * 1024 // 28MB
        if (file.size > maxSize) {
            showToast(t('admin.portfolios.error.too_large'), 'error')
            return false
        }
        return true
    }

    const handlePortfolioUpload = async (file) => {
        if (!validatePdf(file)) return

        setUploadingPortfolio(true)
        try {
            // Check if portfolio exists for this business unit
            const existingPortfolio = portfolios.find(p => p.business_unit === selectedBusinessUnit)

            // Delete old file if exists (using stored file_path for reliability)
            if (existingPortfolio?.file_path) {
                await supabase.storage
                    .from('portfolios')
                    .remove([existingPortfolio.file_path])
            }

            // Generate proper filename based on business unit
            const businessUnitNames = {
                main: 'JDRivero_Portfolio',
                architecture: 'JDRivero_Portfolio_Architecture',
                construction: 'JDRivero_Portfolio_Construction',
                windows: 'JDRivero_Portfolio_Windows',
                roofing: 'JDRivero_Portfolio_Roofing'
            }
            const fileName = `${businessUnitNames[selectedBusinessUnit] || 'JDRivero_Portfolio'}.pdf`

            // Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from('portfolios')
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: true
                })

            if (uploadError) throw uploadError

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('portfolios')
                .getPublicUrl(fileName)

            // Upsert portfolio record (store file_path for reliable deletion)
            const payload = {
                business_unit: selectedBusinessUnit,
                file_url: publicUrl,
                file_path: fileName,
                file_name: file.name,
                file_size: file.size,
                is_active: true,
                updated_at: new Date().toISOString()
            }

            const { error: dbError } = await supabase
                .from('portfolios')
                .upsert(payload, { onConflict: 'business_unit' })

            if (dbError) throw dbError

            await fetchPortfolios()
            // Invalidate cache for this business unit so footer fetches fresh URL
            localStorage.removeItem(`portfolio_${selectedBusinessUnit}`)
            showToast(t('admin.portfolios.upload_success'), 'success')

        } catch (error) {
            console.error('Portfolio upload error:', error)
            showToast(t('admin.portfolios.upload_error') + ': ' + error.message, 'error')
        } finally {
            setUploadingPortfolio(false)
        }
    }

    const handlePortfolioDrag = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setPortfolioDragActive(true)
        } else if (e.type === 'dragleave') {
            setPortfolioDragActive(false)
        }
    }

    const handlePortfolioDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setPortfolioDragActive(false)
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handlePortfolioUpload(e.dataTransfer.files[0])
        }
    }

    const handlePortfolioFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handlePortfolioUpload(e.target.files[0])
        }
    }

    const handleDeletePortfolio = async (businessUnit) => {
        showConfirm(t('admin.portfolios.delete_confirm'), async () => {
            const portfolio = portfolios.find(p => p.business_unit === businessUnit)
            if (!portfolio) return

            try {
                // Delete file from storage (using stored file_path for reliability)
                if (portfolio.file_path) {
                    await supabase.storage
                        .from('portfolios')
                        .remove([portfolio.file_path])
                }

                // Delete record from database
                const { error } = await supabase
                    .from('portfolios')
                    .delete()
                    .eq('business_unit', businessUnit)

                if (error) throw error

                await fetchPortfolios()
                // Invalidate cache for this business unit so footer fetches fresh URL
                localStorage.removeItem(`portfolio_${businessUnit}`)
                showToast(t('admin.portfolios.delete_success'), 'success')
            } catch (error) {
                console.error('Delete portfolio error:', error)
                showToast(t('admin.portfolios.delete_error'), 'error')
            }
        })
    }

    const formatFileSize = (bytes) => {
        if (!bytes) return '-'
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }

    // Fetch posts only when blog or home view is active
    useEffect(() => {
        if (session && (activeView === 'blog' || activeView === 'home')) fetchPosts()
    }, [session, activeView])

    // Fetch portfolios only when portfolios or home view is active
    useEffect(() => {
        if (session && (activeView === 'portfolios' || activeView === 'home')) fetchPortfolios()
    }, [session, activeView])

    const markDashboardRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible
    const { markRefreshed: markDashboardRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (activeView === 'blog' || activeView === 'home') {
                if (!editingPost) {
                    Promise.resolve(fetchPosts(true)).finally(() => markDashboardRefreshedRef.current?.())
                } else {
                    markDashboardRefreshedRef.current?.()
                }
            }
            if (activeView === 'portfolios' || activeView === 'home') {
                Promise.resolve(fetchPortfolios(true)).finally(() => markDashboardRefreshedRef.current?.())
            }
        }, [activeView, editingPost]),
    })
    markDashboardRefreshedRef.current = markDashboardRefreshed

    // --- RENDERERS ---

    // 0. LOADING SCREEN
    if ((loading && session) || roleLoading) {
        return (
            <div className="min-h-screen bg-cloud flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center gap-4"
                >
                    <svg className="animate-spin h-12 w-12 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-ash font-medium animate-pulse">
                        {t('common.loading')}
                    </p>
                </motion.div>
            </div>
        )
    }

// 1. LOGIN SCREEN
if (!session) {
return (
<div className="min-h-screen bg-gradient-to-br from-cloud via-gray-100/20 to-cloud flex items-center justify-center p-4">
<motion.div
initial={{ opacity: 0, y: 20 }}
animate={{ opacity: 1, y: 0 }}
className="w-full max-w-md -mt-24"
>
<div className="bg-white border border-gray-200 rounded-2xl shadow-2xl p-8">
<h2 className="text-3xl font-bold mb-6 text-center text-primary">{t('auth.login')}</h2>
                        <form onSubmit={handleLogin} className="space-y-6">
                            <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                                <label>{t('admin.honeypot')}</label>
                                <input
                                    type="text"
                                    value={honeyPot}
                                    onChange={(e) => setHoneyPot(e.target.value)}
                                    tabIndex="-1"
                                    autoComplete="off"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-ash">{t('auth.email')}</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-cloud text-ink focus:outline-none focus:ring-2 focus:ring-primary transition"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-ash">{t('auth.password')}</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-cloud text-ink focus:outline-none focus:ring-2 focus:ring-primary transition"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-primary text-ink font-bold py-3 rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? t('common.loading_dots') : t('auth.signIn')}
                            </button>
                        </form>
</div>
</motion.div>
</div>
)
}

// 2. EDITOR VIEW
    if (editingPost) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="min-h-screen bg-cloud"
            >
                {/* Blocking overlay while translating */}
                <AnimatePresence>
                    {isTranslating && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm"
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-2xl"
                            >
                                <svg className="animate-spin h-5 w-5 text-indigo-500" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <span className="text-sm font-semibold text-ink">{t('admin.language.translating')}</span>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Header */}
                <header className="sticky top-20 z-40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                        <button
                            onClick={() => {
                                setEditingPost(null)
                                setImagePreview(null)
                            }}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all text-ash shrink-0"
                            title={t('common.return')}
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-xl font-bold text-primary flex-1 min-w-0 truncate">
                            {editingPost === 'new' ? t('admin.createNewPost') : t('admin.editingPost')}
                        </h1>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={(e) => handleSave(e, false)}
                                disabled={savingAction === 'draft'}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-primary/20 bg-primary/5 text-primary rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50"
                            >
                                {savingAction === 'draft' ? (
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : <FaSave />}
                                {t('admin.saveDraft')}
                            </button>
                            <button
                                onClick={(e) => handleSave(e, true)}
                                disabled={savingAction === 'publish'}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold bg-primary text-ink rounded-lg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50"
                            >
                                {savingAction === 'publish' ? (
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : <FaCloudUploadAlt />}
                                {t('admin.publish')}
                            </button>
                        </div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 pb-2">
                        <ServiceHealthBar />
                    </div>
                </header>

                {/* Editor Content */}
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left: Form */}
                        <div className="space-y-6">
                            {/* Title */}
                            {/* Language Tabs */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-200 pb-4">
                                <div className="flex bg-gray-100/50 p-1 rounded-lg border border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('es')}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'es'
                                            ? 'bg-white text-ink shadow-sm ring-1 ring-gray-200'
                                            : 'text-ash hover:text-ink hover:bg-gray-100/50'
                                            }`}
                                    >
                                        <FaLanguage className="inline mr-2" />
                                        {t('admin.language.es')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('en')}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'en'
                                            ? 'bg-white text-ink shadow-sm ring-1 ring-gray-200'
                                            : 'text-ash hover:text-ink hover:bg-gray-100/50'
                                            }`}
                                    >
                                        <FaLanguage className="inline mr-2" />
                                        {t('admin.language.en')}
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleTranslate}
                                    disabled={isTranslating}
                                    className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-sm font-semibold"
                                >
                                    <FaMagic className={`${isTranslating ? 'animate-spin' : 'group-hover:rotate-12'} transition-transform duration-300`} />
                                    {isTranslating
                                        ? t('admin.language.translating')
                                        : activeTab === 'en'
                                            ? t('admin.language.translate_to_es')
                                            : t('admin.language.translate_to_en')
                                    }
                                </button>
                            </div>

                            {/* Title */}
                            <div className="relative">
                                <label className="text-sm font-medium mb-2 text-ink flex justify-between">
                                    <span>{t('admin.title')} <span className="text-ash text-xs font-normal">({activeTab === 'en' ? 'English' : 'Spanish'})</span></span>
                                </label>
                                <input
                                    type="text"
                                    value={activeTab === 'en' ? formData.title_en || formData.title : formData.title_es}
                                    onChange={(e) => handleTitleChange(e.target.value)}
                                    placeholder={t('admin.placeholder.title')}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-primary transition-all text-lg font-medium"
                                    required
                                />

                            </div>

                            {/* Slug (Shown in both tabs, but locked if set in the other) */}
                            <div>
                                <label className="text-sm font-medium mb-2 text-ink flex justify-between items-center">
                                    <span>
                                        {t('admin.urlSlug')}
                                        <span className="ml-2 text-xs text-ash font-normal">
                                            ({t('admin.slugHint')})
                                        </span>
                                    </span>
                                </label>
                                <div className="flex relative group">
                                    <span className={`inline-flex items-center px-4 md:px-6 py-2.5 rounded-l-lg border border-r-0 border-gray-200 bg-gray-100 text-ash text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px] md:max-w-none ${formData.slug && formData.slug_origin !== activeTab ? 'opacity-70' : ''}`}>
                                        {window.location.origin}/blog/
                                    </span>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => handleSlugChange(e.target.value)}
                                        onBlur={async () => {
                                            if (!formData.slug || formData.slug_origin !== activeTab) return
                                            const { data } = await supabase.from('posts').select('id').eq('slug', formData.slug).maybeSingle()
                                            if (data && data.id !== editingPost) {
                                                showToast(t('admin.error.duplicateSlug'), 'error')
                                            }
                                        }}
                                        placeholder={t('admin.placeholder.slug')}
                                        disabled={formData.slug && formData.slug_origin && formData.slug_origin !== activeTab}
                                        className={`flex-1 w-full px-4 py-2.5 rounded-r-lg border border-gray-200 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-primary min-w-0 ${formData.slug && formData.slug_origin && formData.slug_origin !== activeTab ? 'bg-gray-50/50 cursor-not-allowed text-ash' : ''}`}
                                    />
                                    {formData.slug && formData.slug_origin && formData.slug_origin !== activeTab && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 cursor-help" title={t('admin.slugLockedHint', { lang: t(`admin.language.${formData.slug_origin}`) })}>
                                            <FaExclamationCircle />
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-ash mt-1">{t('admin.slugHint')}</p>
                            </div>

                            {/* Image Upload */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-ink">{t('admin.featuredImage')}</label>

                                {!imagePreview ? (
                                    <div
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`
                                            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition
                                            ${dragActive
                                                ? 'border-primary bg-primary/10'
                                                : 'border-gray-200 hover:border-primary/50 hover:bg-gray-100/50'
                                            }
                                            ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        <FaUpload className="mx-auto text-4xl text-ash mb-3" />
                                        <p className="text-ink font-medium mb-1">
                                            {uploadingImage ? t('admin.uploading') : t('admin.dropImage')}
                                        </p>
                                        <p className="text-xs text-ash">
                                            {t('admin.imageFormats')}
                                        </p>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/webp"
                                            onChange={handleFileInput}
                                            className="hidden"
                                            disabled={uploadingImage}
                                        />
                                    </div>
                                ) : (
                                    <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                        <img
                                            src={imagePreview}
                                            alt={t('admin.previewAlt')}
                                            className="w-full h-48 object-cover"
                                        />
                                        <button
                                            onClick={removeImage}
                                            className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Excerpt */}
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2 text-ink">{t('admin.excerpt')}</label>
                                <textarea
                                    value={activeTab === 'en' ? formData.excerpt_en || formData.excerpt : formData.excerpt_es}
                                    onChange={(e) => handleExcerptChange(e.target.value)}
                                    placeholder={t('admin.placeholder.excerpt')}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                    rows="3"
                                />

                            </div>

                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-ink">
                                    <FaTag className="inline mr-1.5 text-primary" />
                                    {t('admin.tags')}
                                </label>
                                <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-gray-200 bg-white min-h-[44px] focus-within:ring-2 focus-within:ring-primary">
                                    {(formData.tags || []).map((tag) => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                className="hover:text-destructive transition-colors ml-0.5"
                                            >
                                                <FaTimes className="text-[10px]" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={handleTagKeyDown}
                                        onBlur={() => { if (tagInput.trim()) handleAddTag(tagInput) }}
                                        placeholder={!formData.tags || formData.tags.length === 0 ? t('admin.placeholder.tags') : ''}
                                        className="flex-1 min-w-[120px] bg-transparent outline-none text-ink text-sm"
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                <FaInfoCircle className="inline" />
                                {t('admin.tagsSharedWarning')}
                            </p>

                            {/* Content */}
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2 text-ink">{t('admin.contentMarkdown')}</label>

                                {/* Markdown Toolbar */}
                                <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-100/50 rounded-lg border border-gray-200">
                                    <button onClick={formatBold} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.bold')}><FaBold /></button>
                                    <button onClick={formatItalic} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.italic')}><FaItalic /></button>
                                    <button onClick={formatHeading} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.heading')}><FaHeading /></button>
                                    <button onClick={formatBulletList} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.bulletList')}><FaListUl /></button>
                                    <button onClick={formatNumberList} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.numberList')}><FaListOl /></button>
                                    <button onClick={formatLink} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.link')}><FaLink /></button>
                                    <button onClick={formatCode} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.code')}><FaCode /></button>
                                    <button onClick={formatQuote} type="button" className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110" title={t('admin.toolbar.quote')}><FaQuoteRight /></button>
                                    <div className="w-px bg-gray-200 mx-1" />
                                    <button
                                        onClick={() => inlineImageInputRef.current?.click()}
                                        type="button"
                                        disabled={uploadingInlineImage}
                                        className="p-2 hover:bg-primary hover:text-ink rounded transition-all duration-150 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed relative"
                                        title={t('admin.toolbar.image')}
                                    >
                                        {uploadingInlineImage ? (
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <FaImage />
                                        )}
                                    </button>
                                    <input
                                        ref={inlineImageInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/webp"
                                        onChange={handleInlineImageInput}
                                        className="hidden"
                                    />
                                </div>

                                <textarea
                                    ref={contentTextareaRef}
                                    value={activeTab === 'en' ? formData.content_en || formData.content : formData.content_es}
                                    onChange={(e) => handleContentChange(e.target.value)}
                                    placeholder={t('admin.placeholder.content')}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-primary resize-none font-mono text-sm"
                                    rows="20"
                                />

                            </div>
                        </div>

                        {/* Right: Preview */}
                        <div className="lg:sticky lg:top-24 lg:self-start mt-6 lg:mt-0">
                            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-lg">
                                <h3 className="text-lg font-bold mb-4 text-primary">{t('admin.livePreview')}</h3>
                                <article className="prose prose-lg max-w-none prose-headings:text-ink prose-p:text-ink prose-strong:text-ink prose-em:text-ink prose-li:text-ink prose-code:text-ink prose-pre:bg-gray-100 prose-pre:text-ink prose-a:text-primary">
                                    <h1 className="text-ink">
                                        {(activeTab === 'en' ? (formData.title_en || formData.title) : formData.title_es) || t('admin.placeholder.title')}
                                    </h1>
                                    {imagePreview && <img src={imagePreview} alt={t('admin.featuredImage')} className="w-full rounded-lg" />}
                                    {(activeTab === 'en' ? (formData.excerpt_en || formData.excerpt) : formData.excerpt_es) && (
                                        <p className="lead italic text-ink">
                                            {activeTab === 'en' ? (formData.excerpt_en || formData.excerpt) : formData.excerpt_es}
                                        </p>
                                    )}
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {(activeTab === 'en' ? (formData.content_en || formData.content) : formData.content_es) || t('admin.placeholder.content')}
                                    </ReactMarkdown>
                                </article>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Floating Actions (Emoji only, Scroll-dependent) */}
                <AnimatePresence>
                    {showFloatingActions && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed bottom-10 right-10 z-[60] flex flex-col gap-3"
                        >
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                    setEditingPost(null)
                                    setImagePreview(null)
                                    window.scrollTo({ top: 0, behavior: 'smooth' })
                                }}
                                className="p-3 bg-white text-ash hover:text-ink rounded-full shadow-2xl border border-gray-100 transition-all text-lg"
                                title={t('common.return')}
                            >
                                ⬅️
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => handleSave(e, false)}
                                className="p-3 bg-white text-primary rounded-full shadow-2xl border border-primary/20 transition-all text-lg"
                                title={t('admin.saveDraft')}
                            >
                                {savingAction === 'draft' ? '⏳' : '💾'}
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => handleSave(e, true)}
                                className="p-4 bg-primary text-ink rounded-full shadow-2xl border border-primary/20 transition-all text-xl"
                                title={t('admin.publish')}
                            >
                                {savingAction === 'publish' ? '⏳' : '🚀'}
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Toast Notification */}
                <AnimatePresence>
                    {toast.show && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed bottom-8 right-8 z-50"
                        >
                            <div className={`
                                flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border backdrop-blur-sm
                                ${toast.type === 'success' ? 'bg-green-50 dark:bg-green-950/90 text-green-900 dark:text-green-100 border-green-200 dark:border-green-800' :
                                    toast.type === 'error' ? 'bg-red-50 dark:bg-red-950/90 text-red-900 dark:text-red-100 border-red-200 dark:border-red-800' :
                                        'bg-blue-50 dark:bg-blue-950/90 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800'}
                            `}>
                                {toast.type === 'success' && <FaCheckCircle className="text-xl" />}
                                {toast.type === 'error' && <FaExclamationCircle className="text-xl" />}
                                {toast.type === 'info' && <FaInfoCircle className="text-xl" />}
                                <span className="font-medium">{toast.message}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        )
    }

    // 3. STORE VIEW
    if (activeView === 'store') {
        if (!permissions['admin.products.access']) {
            return (
                <div className="min-h-screen bg-cloud flex flex-col items-center justify-center p-8 text-center space-y-4">
                    <FaExclamationCircle className="text-5xl text-red-500" />
                    <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                    <p className="text-ash">{t('admin.access.req_store_manager')}</p>
                    <button onClick={() => setActiveView('home')} className="px-6 py-2 bg-primary text-ink rounded-lg font-medium shadow hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                        <FaArrowLeft />
                        {t('common.return_dashboard')}
                    </button>
                </div>
            )
        }
        return (
            <StoreManagement
                session={session}
                onReturn={() => setActiveView('home')}
                onLogout={handleLogout}
            />
        )
    }

    // 4. MONDAY VIEW
    if (activeView === 'monday') {
        if (!permissions['admin.monday.access']) {
            return (
                <div className="min-h-screen bg-cloud flex flex-col items-center justify-center p-8 text-center space-y-4">
                    <FaExclamationCircle className="text-5xl text-red-500" />
                    <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                    <p className="text-ash">{t('admin.access.req_editor')}</p>
                    <button onClick={() => setActiveView('home')} className="px-6 py-2 bg-primary text-ink rounded-lg font-medium shadow hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                        <FaArrowLeft />
                        {t('common.return_dashboard')}
                    </button>
                </div>
            )
        }
        return (
            <MondayManagement
                session={session}
                onReturn={() => setActiveView('home')}
                onLogout={handleLogout}
            />
        )
    }

    // 5. CONSOLE VIEW
    if (activeView === 'console') {
        return (
            <RoleProvider session={session}>
                <UserConsole
                    session={session}
                    onReturn={() => setActiveView('home')}
                    onLogout={handleLogout}
                />
            </RoleProvider>
        )
    }

    // 6. WORKFLOW VIEW
    if (activeView === 'workflow') {
        if (!permissions['admin.workflow.access'] && !isClient) {
            return (
                <div className="min-h-screen bg-cloud flex flex-col items-center justify-center p-8 text-center space-y-4">
                    <FaExclamationCircle className="text-5xl text-red-500" />
                    <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                    <p className="text-ash">{t('admin.access.req_workflow')}</p>
                    <button onClick={() => setActiveView('home')} className="px-6 py-2 bg-primary text-ink rounded-lg font-medium shadow hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                        <FaArrowLeft />
                        {t('common.return_dashboard')}
                    </button>
                </div>
            )
        }
        return (
            <RoleProvider session={session}>
                <WorkflowMap
                    session={session}
                    onReturn={() => setActiveView('home')}
                    onLogout={handleLogout}
                />
            </RoleProvider>
        )
    }

    // 7b. ESTIMATE BUILDER VIEW
if (activeView === 'estimator') {
    if (!permissions['admin.estimator.access']) {
            return (
                <div className="min-h-screen bg-cloud flex flex-col items-center justify-center p-8 text-center space-y-4">
                    <FaExclamationCircle className="text-5xl text-red-500" />
                    <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                    <button onClick={() => setActiveView('home')} className="px-6 py-2 bg-primary text-ink rounded-lg font-medium shadow hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                        <FaArrowLeft />{t('common.return_dashboard')}
                    </button>
                </div>
            )
        }
        return (
            <RoleProvider session={session}>
                <EstimateBuilder
                    session={session}
                    onReturn={() => setActiveView('home')}
                    onLogout={handleLogout}
                />
            </RoleProvider>
        )
    }

    // 7. PORTFOLIO MANAGEMENT VIEW
    if (activeView === 'portfolios') {
        if (!permissions['admin.portfolios.access']) {
            return (
                <div className="min-h-screen bg-cloud flex flex-col items-center justify-center p-8 text-center space-y-4">
                    <FaExclamationCircle className="text-5xl text-red-500" />
                    <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                    <p className="text-ash">{t('admin.access.req_editor')}</p>
                    <button onClick={() => setActiveView('home')} className="px-6 py-2 bg-primary text-ink rounded-lg font-medium shadow hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                        <FaArrowLeft />
                        {t('common.return_dashboard')}
                    </button>
                </div>
            )
        }
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="min-h-screen bg-cloud"
            >
                {/* Header */}
                <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                        <button
                            onClick={() => setActiveView('home')}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all text-ash shrink-0"
                            title={t('common.return')}
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-xl font-bold text-primary flex-1">{t('admin.portfolios.management_title')}</h1>
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all text-ash shrink-0"
                            title={t('auth.signOut')}
                        >
                            <FaSignOutAlt />
                        </button>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 pb-2">
                        <ServiceHealthBar />
                    </div>
                </header>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left: Upload Section */}
                        <div className="space-y-6">
                            {/* Business Unit Selector */}
                            <div>
                                <label className="block text-sm font-medium mb-3 text-ink">
                                    {t('admin.portfolios.select_business_unit')}
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {BUSINESS_UNITS.map((unit) => {
                                        const Icon = unit.icon
                                        const isSelected = selectedBusinessUnit === unit.key
    if (!permissions['admin.blog.access']) {
        return (
            <div className="min-h-screen bg-cloud flex flex-col items-center justify-center p-8 text-center space-y-4">
                <FaExclamationCircle className="text-5xl text-red-500" />
                <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                <button onClick={() => setActiveView('home')} className="px-6 py-2 bg-primary text-ink rounded-lg font-medium shadow hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                    <FaArrowLeft />{t('common.return_dashboard')}
                </button>
            </div>
        );
    }
    return (
                                            <button
                                                key={unit.key}
                                                type="button"
                                                onClick={() => setSelectedBusinessUnit(unit.key)}
                                                className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${isSelected
                                                    ? 'border-primary bg-primary/10 shadow-md'
                                                    : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <Icon className={`text-2xl ${isSelected ? 'text-primary' : 'text-ash'}`} />
                                                <span className={`text-xs font-medium text-center ${isSelected ? 'text-primary' : 'text-ash'}`}>
                                                    {unit.label}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* PDF Upload Zone */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-ink">
                                    {t('admin.portfolios.upload_pdf')}
                                </label>
                                <div
                                    onDragEnter={handlePortfolioDrag}
                                    onDragLeave={handlePortfolioDrag}
                                    onDragOver={handlePortfolioDrag}
                                    onDrop={handlePortfolioDrop}
                                    onClick={() => portfolioFileInputRef.current?.click()}
                                    className={`
                                        relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-200
                                        ${portfolioDragActive
                                            ? 'border-primary bg-primary/10'
                                            : 'border-gray-300 hover:border-primary/50 hover:bg-gray-50'
                                        }
                                        ${uploadingPortfolio ? 'opacity-50 cursor-not-allowed' : ''}
                                    `}
                                >
                                    <FaFilePdf className="mx-auto text-5xl text-red-500 mb-4" />
                                    <p className="text-ink font-medium mb-2">
                                        {uploadingPortfolio ? t('admin.portfolios.uploading') : t('admin.portfolios.drop_pdf')}
                                    </p>
                                    <p className="text-xs text-ash">
                                        {t('admin.portfolios.pdf_only')}
                                    </p>
                                    <input
                                        ref={portfolioFileInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        onChange={handlePortfolioFileInput}
                                        className="hidden"
                                        disabled={uploadingPortfolio}
                                    />
                                </div>
                            </div>

                            {/* Current Portfolio Info */}
                            {portfolios.find(p => p.business_unit === selectedBusinessUnit) && (
                                <div className="bg-white border border-gray-200 rounded-xl p-4">
                                    <h4 className="text-sm font-medium text-ink mb-2">
                                        {t('admin.portfolios.current_portfolio')}
                                    </h4>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <FaFilePdf className="text-red-500 text-xl" />
                                            <div>
                                                <p className="text-sm font-medium text-ink">
                                                    {portfolios.find(p => p.business_unit === selectedBusinessUnit)?.file_name}
                                                </p>
                                                <p className="text-xs text-ash">
                                                    {formatFileSize(portfolios.find(p => p.business_unit === selectedBusinessUnit)?.file_size)}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDeletePortfolio(selectedBusinessUnit)}
                                            className="p-2 rounded-lg border-2 border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200 text-ink shadow-sm hover:shadow hover:scale-110"
                                            title={t('admin.actions.delete')}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: Existing Portfolios Table */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                <h3 className="text-lg font-semibold text-ink">
                                    {t('admin.portfolios.existing_portfolios')}
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-200">
                                {BUSINESS_UNITS.map((unit) => {
                                    const portfolio = portfolios.find(p => p.business_unit === unit.key)
                                    const Icon = unit.icon
                                    return (
                                        <div
                                            key={unit.key}
                                            className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Icon className="text-xl text-ash" />
                                                <div>
                                                    <p className="font-medium text-ink">{unit.label}</p>
                                                    {portfolio ? (
                                                        <p className="text-xs text-ash">
                                                            {portfolio.file_name} • {formatFileSize(portfolio.file_size)}
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs text-amber-600">
                                                            {t('admin.portfolios.no_portfolio')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {portfolio ? (
                                                <button
                                                    onClick={() => handleDeletePortfolio(unit.key)}
                                                    className="p-2 rounded-lg text-ash hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                                                    title={t('admin.actions.delete')}
                                                >
                                                    <FaTrash />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => setSelectedBusinessUnit(unit.key)}
                                                    className="px-3 py-1.5 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-ink transition-all duration-200"
                                                >
                                                    {t('admin.portfolios.add')}
                                                </button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Confirmation Dialog */}
                <AnimatePresence>
                    {confirmDialog.show && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                            onClick={handleCancelConfirm}
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                transition={{ type: "spring", duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white border-2 border-gray-200 rounded-xl p-8 max-w-md w-full shadow-2xl"
                            >
                                <h3 className="text-xl font-bold mb-4 text-ink">{t('admin.confirmAction')}</h3>
                                <p className="text-ash mb-6">{confirmDialog.message}</p>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={handleCancelConfirm}
                                        className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                                    >
                                        {t('common.cancel')}
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        {t('common.confirm')}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Toast Notification */}
                <AnimatePresence>
                    {toast.show && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed bottom-8 right-8 z-50"
                        >
                            <div className={`
                                flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border backdrop-blur-sm
                                ${toast.type === 'success' ? 'bg-green-50 dark:bg-green-950/90 text-green-900 dark:text-green-100 border-green-200 dark:border-green-800' :
                                    toast.type === 'error' ? 'bg-red-50 dark:bg-red-950/90 text-red-900 dark:text-red-100 border-red-200 dark:border-red-800' :
                                        'bg-blue-50 dark:bg-blue-950/90 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800'}
                            `}>
                                {toast.type === 'success' && <FaCheckCircle className="text-xl" />}
                                {toast.type === 'error' && <FaExclamationCircle className="text-xl" />}
                                {toast.type === 'info' && <FaInfoCircle className="text-xl" />}
                                <span className="font-medium">{toast.message}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        )
    }

    // 7. HOME VIEW — section cards
    if (activeView === 'home') {
        // Auto-redirect estimator-only users directly to the Estimate Builder
        if (isAnyEstimator && !permissions['admin.blog.access'] && !permissions['admin.products.access'] && !permissions['admin.portfolios.access'] && !permissions['admin.monday.access'] && !permissions['admin.workflow.access'] && !permissions['admin.gallery.access'] && !permissions['admin.storage.access'] && !permissions['admin.console.access']) {
            return (
                <RoleProvider session={session}>
                    <EstimateBuilder
                        session={session}
                        onReturn={handleLogout}
                        onLogout={handleLogout}
                    />
                </RoleProvider>
            )
        }

        const allSections = [
            {
                key: 'blog',
                icon: FaEdit,
                label: t('nav.blog'),
                desc: t('admin.sections.blog_desc'),
                color: 'border-primary/30 hover:border-primary bg-white',
                iconColor: 'text-primary',
            },
            {
                key: 'store',
                icon: FaStore,
                label: t('admin.store.title'),
                desc: t('admin.sections.store_desc'),
                color: 'border-primary/30 hover:border-primary bg-white',
                iconColor: 'text-primary',
            },
            {
                key: 'portfolios',
                icon: FaFilePdf,
                label: t('admin.portfolios.title'),
                desc: t('admin.sections.portfolios_desc'),
                color: 'border-primary/30 hover:border-primary bg-white',
                iconColor: 'text-primary',
            },
            {
                key: 'monday',
                icon: FaThList,
                label: t('admin.monday.title'),
                desc: t('admin.sections.monday_desc'),
                color: 'border-purple-200 hover:border-purple-400 bg-white',
                iconColor: 'text-purple-600',
            },
            {
                key: 'workflow',
                icon: FaProjectDiagram,
                label: t('admin.workflow.title'),
                desc: t('admin.sections.workflow_desc'),
                color: 'border-amber-200 hover:border-amber-400 bg-white',
                iconColor: 'text-amber-600',
            },
            {
                key: 'estimator',
                icon: FaFileInvoiceDollar,
                label: t('admin.sections.estimator_label'),
                desc: t('admin.sections.estimator_desc'),
                color: 'border-violet-200 hover:border-violet-400 bg-white',
                iconColor: 'text-violet-600',
            },
            {
                key: 'gallery',
                icon: FaImages,
                label: t('admin.sections.gallery'),
                desc: t('admin.sections.gallery_desc'),
                color: 'border-teal-200 hover:border-teal-400 bg-white',
                iconColor: 'text-teal-600',
                navigate: '/admin/gallery',
            },
            {
                key: 'storage',
                icon: FaDatabase,
                label: t('admin.sections.storage'),
                desc: t('admin.sections.storage_desc'),
                color: 'border-indigo-200 hover:border-indigo-400 bg-white',
                iconColor: 'text-indigo-600',
                navigate: '/admin/storage',
            },
            {
                key: 'console',
                icon: FaUserShield,
                label: t('admin.sections.console'),
                desc: t('admin.sections.console_desc'),
                color: 'border-red-200 hover:border-red-400 bg-white',
                iconColor: 'text-red-600',
            },
        ]

        const SECTIONS = allSections.filter(s => {
            switch (s.key) {
                case 'blog': return permissions['admin.blog.access']
                case 'store': return permissions['admin.products.access']
                case 'portfolios': return permissions['admin.portfolios.access']
                case 'monday': return permissions['admin.monday.access']
                case 'workflow': return permissions['admin.workflow.access'] || isClient
                case 'estimator': return permissions['admin.estimator.access']
                case 'gallery': return permissions['admin.gallery.access']
                case 'storage': return permissions['admin.storage.access']
                case 'console': return permissions['admin.console.access']
                default: return false
            }
        })

        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="min-h-screen bg-cloud"
            >
                <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                        <h1 className="text-xl font-bold text-primary flex-1">{t('admin.dashboard')}</h1>
                        <span className="hidden sm:inline-flex items-center px-2 py-2 rounded-lg text-[10px] font-mono font-semibold bg-gray-100 text-gray-500 border border-gray-200 select-none leading-none" title="App version">
                            v{__APP_VERSION__}
                        </span>
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all text-ash shrink-0"
                            title={t('auth.signOut')}
                        >
                            <FaSignOutAlt />
                        </button>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 pb-2">
                        <ServiceHealthBar />
                    </div>
                </header>

                {/* Mobile: button list */}
                <div className="md:hidden flex flex-col gap-2 px-4 py-4">
                    {SECTIONS.map(({ key, icon: Icon, label, desc, color, iconColor, navigate: navPath }) => (
                        <button
                            key={key}
                            onClick={() => navPath ? navigate(navPath) : setActiveView(key)}
                            className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 hover:shadow-sm ${color}`}
                        >
                            <Icon className={`text-xl shrink-0 ${iconColor}`} />
                            <div className="flex-1 text-left min-w-0">
                                <p className="font-semibold text-ink text-sm leading-tight">{label}</p>
                                <p className="text-xs text-ash leading-tight">{desc}</p>
                            </div>
                            <FaChevronRight className="text-xs text-ash shrink-0" />
                        </button>
                    ))}
                </div>

                {/* iPad + Desktop: card grid */}
                <div className="hidden md:block max-w-2xl lg:max-w-4xl mx-auto px-4 py-8 lg:py-12">
                    <div className="grid md:grid-cols-3 gap-3 lg:gap-4">
                        {SECTIONS.map(({ key, icon: Icon, label, desc, color, iconColor, navigate: navPath }) => (
                            <button
                                key={key}
                                onClick={() => navPath ? navigate(navPath) : setActiveView(key)}
                                className={`flex flex-col items-center gap-3 p-5 lg:p-8 rounded-xl lg:rounded-2xl border-2 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${color}`}
                            >
                                <Icon className={`text-2xl lg:text-3xl ${iconColor}`} />
                                <div className="text-center">
                                    <p className="font-semibold text-ink text-sm lg:text-base leading-tight">{label}</p>
                                    <p className="text-xs text-ash mt-0.5 leading-tight">{desc}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </motion.div>
        )
    }

    // 8. BLOG VIEW — posts list
return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="min-h-screen bg-cloud"
        >
            {/* Header */}
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => setActiveView('home')}
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all text-ash shrink-0"
                        title={t('common.return')}
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-xl font-bold text-primary flex-1">{t('nav.blog')}</h1>
                    {permissions['admin.blog.access'] && (
                        <button
                            onClick={handleCreateNew}
                            className="px-3 py-1.5 text-sm bg-primary text-ink hover:bg-primary/90 transition-all flex items-center gap-2 font-semibold rounded-lg shrink-0"
                        >
                            <FaPlus />
                            {t('admin.newPost')}
                        </button>
                    )}
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-lg border border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all text-ash shrink-0"
                        title={t('auth.signOut')}
                    >
                        <FaSignOutAlt />
                    </button>
                </div>
                <div className="max-w-7xl mx-auto px-4 pb-2">
                    <ServiceHealthBar />
                </div>
            </header>

            {/* Search */}
            <div className="max-w-7xl mx-auto px-4 py-4">
                <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-ash" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('admin.searchPlaceholder', 'Search posts...')}
                        className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
            </div>

            {/* Posts List */}
            <div className="max-w-7xl mx-auto px-4 pb-12">
                {filteredPosts.length === 0 ? (
                    <div className="text-center py-20 text-ash">
                        <p className="text-xl">{t('admin.noPosts')}</p>
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="w-full min-w-[520px]">
                            <thead className="bg-gray-100/50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-ash uppercase tracking-wider">
                                        {t('admin.table.title')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-ash uppercase tracking-wider">
                                        {t('admin.table.status')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-ash uppercase tracking-wider">
                                        {t('admin.table.date')}
                                    </th>
                                    {permissions['admin.blog.access'] && (
                                        <th className="px-6 py-3 text-right text-xs font-medium text-ash uppercase tracking-wider">
                                            {t('admin.table.actions')}
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredPosts.map((post) => (
                                    <tr key={post.id} className="hover:bg-gray-100/30 transition">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {post.image_url && (
                                                    <img
                                                        src={post.image_url}
                                                        alt={t('admin.table.thumbnail')}
                                                        className="w-12 h-12 rounded object-cover"
                                                    />
                                                )}
                                                <div>
                                                    <p className="font-medium text-ink">{post.title_en || post.title_es || post.title || 'Untitled'}</p>
                                                    <p className="text-sm text-ash font-mono">{post.slug}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-sm ${post.is_published ? 'text-green-600 dark:text-green-400 font-medium' : 'text-amber-600 dark:text-amber-400 font-medium'}`}>
                                                {post.is_published ? t('admin.published') : t('admin.draft')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-ash">
                                            {new Date(post.created_at).toLocaleDateString(i18n.language)}
                                        </td>
                                        {permissions['admin.blog.access'] && (
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(post)}
                                                        className="p-2 rounded-lg border-2 border-gray-200 hover:bg-primary hover:text-ink hover:border-primary transition-all duration-200 text-ink shadow-sm hover:shadow hover:scale-110"
                                                        title={t('admin.actions.edit')}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(post.id)}
                                                        className="p-2 rounded-lg border-2 border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200 text-ink shadow-sm hover:shadow hover:scale-110"
                                                        title={t('admin.actions.delete')}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            <AnimatePresence>
                {confirmDialog.show && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={handleCancelConfirm}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ type: "spring", duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border-2 border-gray-200 rounded-xl p-8 max-w-md w-full shadow-2xl"
                        >
                            <h3 className="text-xl font-bold mb-4 text-ink">{t('admin.confirmAction')}</h3>
                            <p className="text-ash mb-6">{confirmDialog.message}</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={handleCancelConfirm}
                                    className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                >
                                    {t('common.confirm')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toast Notification */}
            <AnimatePresence>
                {toast.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed bottom-8 right-8 z-50"
                    >
                        <div className={`
                            flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border backdrop-blur-sm
                            ${toast.type === 'success' ? 'bg-green-50 dark:bg-green-950/90 text-green-900 dark:text-green-100 border-green-200 dark:border-green-800' :
                                toast.type === 'error' ? 'bg-red-50 dark:bg-red-950/90 text-red-900 dark:text-red-100 border-red-200 dark:border-red-800' :
                                    'bg-blue-50 dark:bg-blue-950/90 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800'}
                        `}>
                            {toast.type === 'success' && <FaCheckCircle className="text-xl" />}
                            {toast.type === 'error' && <FaExclamationCircle className="text-xl" />}
                            {toast.type === 'info' && <FaInfoCircle className="text-xl" />}
                            <span className="font-medium">{toast.message}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div >
    )
}
